import { useEffect, useRef, useState } from "react";
import { captureToolEmail, TOOL_SLUGS, warmCaptureFunction } from "@/lib/tools/tool-capture";
import { trackMetaEvent } from "@/lib/meta-pixel";
import { suggestEmail } from "@/lib/email-typo";
import { useTurnstile } from "@/lib/turnstile";
import { STORM_OFFER } from "./storm-landing";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The sign-up box, in the order a reader decides: what they get, the field,
 * the button, then what removes the risk and the proof line.
 * Everything in it is the same on every version except `outcome`, the
 * version's dream outcome, which leads the box when set. `landingVariant` says
 * which page the sign-up came from.
 */
export function StormOfferCard({
  landingVariant,
  outcome = "",
  review = false,
}: {
  landingVariant: string;
  outcome?: string;
  /** The review link: the sign-up still works, but is never sent to Meta. */
  review?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [trap, setTrap] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);
  const turnstile = useTurnstile();

  useEffect(() => {
    warmCaptureFunction();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (submittingRef.current) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      await captureToolEmail(trimmed, TOOL_SLUGS.stormLanding, {
        landing_variant: landingVariant,
        website: trap,
        turnstile_token: await turnstile.getToken(),
      });
      // Only after the lead is saved: a Lead the ad platform counts but our
      // table never got would make the ads look better than they are.
      if (!review) trackMetaEvent("Lead");
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      turnstile.reset();
      setLoading(false);
      submittingRef.current = false;
    }
  }

  return (
    <section className="mt-5 rounded-xl border border-primary/30 bg-primary/10 p-4 sm:mt-6 sm:p-6">
      <OfferHeading outcome={outcome} />

      {done ? (
        <div role="status" className="mt-5">
          <p className="text-lg font-semibold">{STORM_OFFER.thanksTitle}</p>
          {STORM_OFFER.download && (
            <a
              href={STORM_OFFER.download.path}
              download
              className="mt-4 block w-full rounded-lg bg-primary px-6 py-3 text-center text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {STORM_OFFER.download.label}
            </a>
          )}
          <p className="mt-3 text-sm text-muted-foreground">{STORM_OFFER.thanksBody}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="relative mt-4 space-y-3 sm:mt-5" noValidate>
          {/* The bot trap: off-screen rather than display:none, which some
              bots skip, and out of reach of the keyboard, autofill and screen
              readers, so no person ever fills it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={trap}
              onChange={(e) => setTrap(e.target.value)}
            />
          </div>
          <label htmlFor="storm-email" className="sr-only">
            Email address
          </label>
          <input
            id="storm-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
              setHint(null);
            }}
            onBlur={() => setHint(suggestEmail(email.trim()))}
            placeholder="you@example.com"
            className="w-full rounded-lg border-2 border-foreground/40 bg-background px-4 py-3 text-base text-foreground shadow-sm placeholder:text-foreground/55 focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40"
          />
          {hint && (
            <button
              type="button"
              onClick={() => {
                setEmail(hint);
                setHint(null);
              }}
              className="block cursor-pointer text-left text-sm text-foreground underline underline-offset-2"
            >
              Did you mean {hint}?
            </button>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full cursor-pointer rounded-lg bg-primary px-6 py-3 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? STORM_OFFER.busy : STORM_OFFER.button}
          </button>
          <div className="text-center text-sm">
            <p className="font-medium">{STORM_OFFER.assurance}</p>
            <p className="mt-1 text-muted-foreground">{STORM_OFFER.proof}</p>
          </div>
          <div ref={turnstile.ref} />
          {STORM_OFFER.delivery && (
            <p className="text-xs text-muted-foreground">{STORM_OFFER.delivery}</p>
          )}
          <p className="text-xs text-muted-foreground">{STORM_OFFER.finePrint}</p>
        </form>
      )}
      {STORM_OFFER.body && (
        <p className="mt-4 text-sm text-muted-foreground">{STORM_OFFER.body}</p>
      )}
    </section>
  );
}

/**
 * The guide's cover beside the title once the guide exists; the title alone
 * before. With an outcome, the outcome is the heading and the free month
 * follows it, rather than the free month leading.
 */
function OfferHeading({ outcome }: { outcome: string }) {
  const text = (
    <div>
      {outcome ? (
        <>
          <h2 className="font-heading text-xl font-bold sm:text-2xl">{outcome}</h2>
          <p className="mt-1 text-sm font-semibold sm:text-base">{STORM_OFFER.withOutcome}</p>
        </>
      ) : (
        <>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            {STORM_OFFER.eyebrow}
          </p>
          <h2 className="mt-1 font-heading text-xl font-bold sm:mt-2 sm:text-2xl">
            {STORM_OFFER.title}
          </h2>
        </>
      )}
      {!outcome && STORM_OFFER.subtitle && (
        <p className="mt-1 text-sm font-semibold sm:text-base">{STORM_OFFER.subtitle}</p>
      )}
      <ul className="mt-2 space-y-0.5 text-sm">
        {STORM_OFFER.points.map((point) => (
          <li key={point} className="flex gap-2">
            <span aria-hidden="true" className="font-bold text-primary">
              ✓
            </span>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  if (!STORM_OFFER.cover) return text;

  return (
    <div className="flex items-start gap-3">
      <img
        src={STORM_OFFER.cover}
        alt={`Cover of ${STORM_OFFER.title}`}
        className="w-16 shrink-0 shadow-md sm:w-20"
      />
      {text}
    </div>
  );
}
